import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { animated, useTransition } from '@react-spring/web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import { useAppSelector } from '@/redux/hooks';
import { getDeployment } from '@/redux/modules/config';
import { getLiveCatalogCurrentItemId } from '@/redux/modules/console';
import { getItemSummariesForCatalog } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemSummary } from '@/types/item/ItemSummary';

import ListItem from './components/ListItem/ListItem';
import { useCalculatedRelativeFavoritesPosition } from './hooks/useCalculatedRelativeFavoritesPosition';
import { useFindLiveItemIndex } from './hooks/useFindLiveItemIndex';

type Props = {
    catalogId: number;
};

const AuctionLotsScroller = ({ catalogId }: Props) => {
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const items = useAppSelector((state) => getItemSummariesForCatalog(state, catalogId));
    const deployment = useAppSelector(getDeployment);
    const { formatMessage } = useIntl();

    // sometimes the itemSummary index isn't the same as it's position in the list so we need to find it manually
    const liveItemIndex = useFindLiveItemIndex({ catalogId, itemId });
    const [isLiveItemVisible, setIsLiveItemVisible] = useState<boolean>(true);

    // Used to see if it should autoscroll
    const [indexDifference, setIndexDifference] = useState<number>(liveItemIndex);

    const lotsUntilUpcomingFavorites = useCalculatedRelativeFavoritesPosition({
        catalogId,
        itemId,
    });

    const virtuoso = useRef<VirtuosoHandle>(null);

    const handleScrollToLiveItem = useCallback(() => {
        virtuoso?.current?.scrollToIndex?.({
            align: 'start',
            behavior: 'smooth',
            index: liveItemIndex,
        });
    }, [liveItemIndex]);

    // Handle if react-virtuoso scrolls, track if live item is visible or not
    const handleOnRangeChanged = ({ endIndex, startIndex }) => {
        if (liveItemIndex >= startIndex && liveItemIndex <= endIndex) {
            setIsLiveItemVisible(true);
        } else {
            setIsLiveItemVisible(false);
            setIndexDifference(liveItemIndex);
        }
    };

    const backToLiveButtonTransition = useTransition(!isLiveItemVisible, {
        enter: { opacity: 1 },
        from: { opacity: 0 },
        leave: { opacity: 0 },
    });

    useEffect(() => {
        if (isLiveItemVisible && indexDifference !== liveItemIndex) {
            handleScrollToLiveItem();
            setIndexDifference(liveItemIndex);
        }
    }, [liveItemIndex, isLiveItemVisible, handleScrollToLiveItem, indexDifference]);

    return (
        <div
            className={cn(
                'relative h-full overflow-hidden border-border-secondary bg-background-primary',
                !isLiveItemVisible ? 'border-t-0' : 'border-t'
            )}
            data-testid="auctionLotScroller"
        >
            {backToLiveButtonTransition(
                (styles, item) =>
                    item && (
                        <animated.div
                            style={styles}
                            className="absolute left-0 right-0 top-2xs z-[3] flex items-center border-b border-border-secondary bg-background-primary p-sm pt-3xs"
                        >
                            <Button
                                onClick={() => handleScrollToLiveItem()}
                                variant="outlined"
                                className="w-full"
                            >
                                {formatMessage({
                                    id: 'bidding_console.console.button.backToLiveItem',
                                })}
                            </Button>
                        </animated.div>
                    )
            )}
            <Virtuoso
                data={items}
                // Used for tests
                data-testid="virtuosoScrollList"
                initialItemCount={deployment === 'test' ? 4 : undefined}
                initialTopMostItemIndex={liveItemIndex}
                itemContent={(_index, item: ItemSummary) => (
                    <ListItem
                        index={_index}
                        isLiveItemVisible={isLiveItemVisible}
                        itemId={item.itemId}
                        liveItemId={itemId}
                        liveItemIndex={liveItemIndex}
                        lotsUntilUpcoming={lotsUntilUpcomingFavorites[itemId]}
                    />
                )}
                rangeChanged={handleOnRangeChanged}
                ref={virtuoso}
            />
        </div>
    );
};

export default AuctionLotsScroller;
